import { Filter } from '../models/filter';
import { Sorter } from '../models/sorter';

export interface QueryOptionsParam {
    expands?: string[];
    selects?: string[];
    filters?: Filter[];
    filtersString?: string;
    sorters?: Sorter[];
    count?: boolean;
    activityIndicator?: boolean;
    skip?: number;
    top?: number | null;
    format?: string;
    url?: string;
    resultClass?: string;
    resultCardinality?: 'one' | 'many';
}

export class QueryOptions implements QueryOptionsParam {
    expands: string[];
    selects: string[];
    filters: Filter[];
    filtersString: string;
    sorters: Sorter[];
    count: boolean;
    activityIndicator: boolean;
    skip: number;
    top: number | null;
    format?: string;
    url?: string;
    resultClass?: string;
    resultCardinality?: 'one' | 'many';

    constructor(url?: string, options?: QueryOptionsParam) {
        this.expands = !!options?.expands ? options.expands : [];
        this.selects = !!options?.selects ? options.selects : [];
        this.filters = !!options?.filters ? options.filters : [];
        this.filtersString = !!options?.filtersString ? options.filtersString : '';
        this.sorters = !!options?.sorters ? options.sorters : [];
        this.count = !!options?.count ? options.count : false;
        this.activityIndicator = typeof options?.activityIndicator === 'undefined' ? true : options.activityIndicator;
        this.skip = !!options?.skip ? options.skip : 0;
        this.top = !!options?.top ? options.top : null;
        this.format = options?.format;
        this.url = !!options?.url ? options.url : url;
        this.resultClass = options?.resultClass;
        this.resultCardinality = options?.resultCardinality || 'many';
    }

    /**
     * Construit un QueryOptions à partir d'une URL contenant des paramètres OData
     * @param fullUrl URL complète avec paramètres (ex: "api/invoices?$filter=status eq 'active'&$skip=0&$top=10")
     * @returns Une instance de QueryOptions
     */
    public static fromUrlString(fullUrl: string): QueryOptions {
        // Séparer l'URL de base des paramètres
        const [_, queryString] = fullUrl.split('?');

        const options: QueryOptionsParam = {
            url: undefined
        };

        if (!queryString) {
            return new QueryOptions(undefined, options);
        }

        // Parser les paramètres
        const params = new URLSearchParams(queryString);

        // $expand
        if (params.has('$expand')) {
            options.expands = params.get('$expand')!.split(',');
        }

        // $select
        if (params.has('$select')) {
            options.selects = params.get('$select')!.split(',');
        }

        // $filter
        if (params.has('$filter')) {
            const filterString = params.get('$filter')!;
            options.filtersString = filterString;
            // Note: La conversion en objets Filter pourrait être ajoutée ici si nécessaire
            // mais c'est complexe car il faudrait parser la syntaxe OData
        }

        // $orderby
        if (params.has('$orderby')) {
            const orderByString = params.get('$orderby')!;
            options.sorters = [];
            const sorterStrings = orderByString.split(',');

            for (const sorterStr of sorterStrings) {
                const trimmed = sorterStr.trim();
                const descending = trimmed.endsWith(' desc');
                const property = descending ? trimmed.substring(0, trimmed.length - 5).trim() : trimmed;

                options.sorters.push(new Sorter({
                    property: property,
                    descending: descending
                }));
            }
        }

        // $skip
        if (params.has('$skip')) {
            options.skip = parseInt(params.get('$skip')!, 10);
        }

        // $top
        if (params.has('$top')) {
            options.top = parseInt(params.get('$top')!, 10);
        }

        // $count, toujours vrai
        options.count = true;

        return new QueryOptions(undefined, options);
    }

    /**
     * Ajoute un filtre programmatique en le concaténant avec les filtres existants (URL ou autres).
     * Utiliser cette méthode pour ajouter des filtres "en dur" sans écraser les filtres de la barre de filtres.
     *
     * @param filter Le filtre à ajouter
     *
     * @example
     * // Dans buildQueryOptions() d'un composant page :
     * protected override buildQueryOptions(): void {
     *     super.buildQueryOptions();
     *     const filter = new Filter({ property: 'status', value: 'active' });
     *     this.queryOptions.addFilter(filter);
     * }
     */
    public addFilter(filter: Filter): void {
        const filterString = filter.toString();
        if (this.filtersString) {
            this.filtersString = `(${this.filtersString}) and (${filterString})`;
        } else {
            this.filtersString = filterString;
        }
    }

    public toUrlString(): string {
        let url = this.url || '';
        let hasOptions: boolean = false;

        if (this.expands.length > 0) {
            url += hasOptions ? '&' : '?';
            url += '$expand=';
            url += this.expands.join(',');
            hasOptions = true;
        }

        if (this.selects.length > 0) {
            url += hasOptions ? '&' : '?';
            url += '$select=';
            url += this.selects.join(',');
            hasOptions = true;
        }

        if (this.filters.length > 0) {
            url += hasOptions ? '&' : '?';
            url += Filter.toUrlString(this.filters);
            hasOptions = true;
        } else if (this.filtersString !== '') {
            url += hasOptions ? '&' : '?';
            url += '$filter=';
            url += this.filtersString;
            hasOptions = true;
        }

        if (this.sorters.length > 0) {
            url += hasOptions ? '&' : '?';
            url += Sorter.toUrlString(this.sorters);
            hasOptions = true;
        }

        if (this.skip) {
            url += hasOptions ? '&' : '?';
            url += '$skip=';
            url += this.skip;
            hasOptions = true;
        }

        if (this.top) {
            url += hasOptions ? '&' : '?';
            url += '$top=';
            url += this.top;
            hasOptions = true;
        }

        if (this.format) {
            url += hasOptions ? '&' : '?';
            url += '$format=';
            url += this.format;
            hasOptions = true;
        }

        if (this.count) {
            url += hasOptions ? '&' : '?';
            url += '$count=true';
        }

        return url;
    }
}
